import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../../redux/AuthSlice/authSlice';
import { AlertCircle, BarChart3, Eye, EyeOff, Handshake, Lock, Mail, Search } from 'lucide-react';

import CareerChampWordmark from '../../components/Brand/CareerChampWordmark';
import api from '../../api/axios';
import LegalModal from '../../components/Modals/LegalModal';

const DISPOSABLE_EMAIL_MESSAGE =
    'Temporary or disposable email addresses are not allowed. Please use a permanent email address.';

const FEATURES = [
    { icon: <Search size={15} />, text: 'Discover companies about to hire — before jobs go public' },
    { icon: <Handshake size={15} />, text: 'Get insider referrals straight to the hiring manager' },
    { icon: <BarChart3 size={15} />, text: 'Track every conversation in one place' },
];

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { user, loginSource } = useSelector((state) => state.auth);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [signingIn, setSigningIn] = useState(false);
    const [legalModal, setLegalModal] = useState(null);
    const [linkedInToast, setLinkedInToast] = useState(false);

    const getLoginErrorMessage = (message) => {
        const value = String(message || '').trim();
        const lowerValue = value.toLowerCase();
        if (lowerValue.includes('already registered')) return 'Email is already registered. Please log in or use forgot password.';
        if (lowerValue.includes('network')) return 'We could not connect to the server. Please check your internet connection and try again.';
        return value || 'Incorrect email or password. Please try again.';
    };

    useEffect(() => {
        if (user && loginSource === 'mongo') {
            navigate('/');
            window.location.reload();
        }
    }, [user, loginSource, navigate]);

    // Surface OAuth failures redirected back from the backend (?error=...).
    useEffect(() => {
        const error = new URLSearchParams(location.search).get('error');
        if (!error) return;
        if (error === 'disposable_email') {
            setLoginError(DISPOSABLE_EMAIL_MESSAGE);
        } else if (error === 'google_auth_failed' || error === 'linkedin_auth_failed') {
            setLoginError('We could not sign you in with that provider. Please try again.');
        } else if (error === 'permission_denied') {
            setLoginError('Google did not grant the permissions we asked for. Please try again and accept the requested access.');
        } else if (error === 'csrf_detected') {
            /**
             * The backend bounces here when the OAuth callback cannot verify the
             * state it issued. Two codes redirected to this page with NO message
             * at all, so a failed connect looked exactly like nothing happening —
             * the user pressed Connect, was returned to the app, and had no way to
             * learn that the grant never landed.
             *
             * The most common cause is a callback that returns to a DIFFERENT host
             * from the one that started the flow (GOOGLE_CALLBACK_URL pointing at a
             * deployed backend while the app runs locally), because the signed
             * handoff cookie is scoped to the host that issued it.
             */
            setLoginError('We could not complete that sign-in securely. Please try again — if it keeps happening, the sign-in callback may be pointing at a different environment.');
        } else if (error === 'internal_error') {
            setLoginError('Something went wrong while signing you in. Please try again.');
        }
    }, [location.search]);

    const handleLogin = (e) => {
        e.preventDefault();
        setLoginError('');
        setSigningIn(true);
        dispatch(login({ email: email.trim().toLowerCase(), password }))
            .unwrap()
            .catch((err) => setLoginError(getLoginErrorMessage(err)))
            .finally(() => setSigningIn(false));
    };

    const handleGoogleLogin = () => {
        const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin;
        window.open(`${baseURL}/api/auth/google`, '_self');
    };

    const handleLinkedInLogin = () => {
        const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin;
        window.open(`${baseURL}/api/auth/linkedin`, '_self');
    };

    return (
        <>
        <div className="h-screen overflow-y-auto flex flex-col md:flex-row">

            {/* ── Left panel — marketing ── */}
            <div
                className="hidden md:flex md:w-1/2 min-h-screen flex-col p-14"
                style={{ background: 'linear-gradient(155deg, #1A2454 0%, #3F57E0 55%, #6B7CF5 100%)' }}
            >
                {/* Logo — top-left */}
                <div className="flex items-center gap-3">
                    <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif", fontWeight: 800, fontStyle: 'italic', color: 'white', fontSize: 17 }}>CC</span>
                    </div>
                    <CareerChampWordmark variant="light" className="text-[30px]" />
                </div>

                {/* Spacer — fixed ~22% of viewport so headline stays in the left-lower area */}
                <div style={{ height: 'calc(100vh * 0.27)', flexShrink: 0 }} />

                {/* Headline + features — anchored in left area */}
                <div className="mb-auto text-left">
                    <h1 className="text-4xl font-bold text-white leading-tight mb-4">
                        Skip the Resume<br />Black Hole
                    </h1>
                    <p className="text-blue-200 text-sm mb-8 leading-relaxed max-w-sm">
                        Get referred to hiring managers at companies about to hire — before the job is even posted.
                    </p>
                    <div className="space-y-4">
                        {FEATURES.map((f, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                                    style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
                                    {f.icon}
                                </div>
                                <span className="text-blue-100 text-sm">{f.text}</span>
                            </div>
                        ))}
                    </div>
                </div>

            </div>

            {/* ── Right panel — form ── */}
            <div className="w-full md:w-1/2 min-h-screen flex flex-col bg-gray-50">

                {/* Top-right link */}
                <div className="flex justify-end p-6">
                    <button
                        type="button"
                        onClick={() => navigate('/signup')}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        New here?{' '}
                        <span className="font-semibold" style={{ color: '#3F57E0' }}>Create account →</span>
                    </button>
                </div>

                {/* Centered form */}
                <div className="flex-1 flex items-center justify-center px-8 pb-8">
                    <div className="w-full max-w-md text-left">

                        <div className="mb-8">
                            <h2 className="text-3xl font-bold text-gray-900 mb-1">Welcome back</h2>
                            <p className="text-gray-500 text-sm">Sign in to your Career Champ workspace</p>
                        </div>

                        <form className="space-y-5" onSubmit={handleLogin}>

                            {/* Email */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Email
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                        <Mail className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        id="email"
                                        type="email"
                                        className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                                        style={{ '--tw-ring-color': '#3F57E033' }}
                                        placeholder="name@company.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Password */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                        Password
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/forgotpass')}
                                        className="text-sm font-medium hover:underline"
                                        style={{ color: '#3F57E0' }}
                                    >
                                        Forgot?
                                    </button>
                                </div>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                        <Lock className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        className="block w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                                        placeholder="••••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center"
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword
                                            ? <EyeOff className="h-4 w-4 text-gray-400" />
                                            : <Eye className="h-4 w-4 text-gray-400" />}
                                    </button>
                                </div>
                            </div>

                            {/* Remember me */}
                            <div className="flex items-center gap-2">
                                <input
                                    id="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300"
                                    style={{ accentColor: '#3F57E0' }}
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                                <label htmlFor="remember-me" className="text-sm text-gray-600">
                                    Keep me signed in for 30 days
                                </label>
                            </div>

                            {/* Error */}
                            {loginError && (
                                <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                    <div>
                                        <p className="font-semibold">Unable to sign in</p>
                                        <p className="mt-0.5 leading-5">{loginError}</p>
                                        <button
                                            type="button"
                                            onClick={() => navigate('/forgotpass')}
                                            className="mt-1 font-medium text-red-800 hover:underline"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Sign in */}
                            <button
                                type="submit"
                                disabled={signingIn}
                                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed"
                                style={{ backgroundColor: '#3F57E0' }}
                            >
                                {signingIn ? (
                                    <>
                                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        Signing in…
                                    </>
                                ) : (
                                    <>Sign in →</>
                                )}
                            </button>

                            {/* OR */}
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-200" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase tracking-wider">
                                    <span className="px-3 bg-gray-50 text-gray-400">or</span>
                                </div>
                            </div>

                            {/* Google */}
                            <button
                                type="button"
                                onClick={handleGoogleLogin}
                                className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-200 rounded-xl bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                <img
                                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                    alt="Google"
                                    className="h-4 w-4"
                                />
                                Continue with Google
                            </button>

                            {/* LinkedIn */}
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => { setLinkedInToast(true); setTimeout(() => setLinkedInToast(false), 3000); }}
                                    className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-xl text-sm font-medium text-white transition-opacity shadow-sm"
                                    style={{ backgroundColor: '#0A66C2', opacity: 0.7, cursor: 'default' }}
                                >
                                    <svg className="h-4 w-4 fill-white flex-shrink-0" viewBox="0 0 24 24">
                                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                    </svg>
                                    Continue with LinkedIn
                                </button>
                                {linkedInToast && (
                                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-9 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold font-dmsans text-white shadow-lg whitespace-nowrap"
                                        style={{ background: '#0F172A' }}>
                                        <span>🚧</span> LinkedIn login coming soon
                                    </div>
                                )}
                            </div>

                        </form>

                        {/* Legal */}
                        <div className="mt-8 text-center">
                            <p className="text-xs text-gray-400">
                                By continuing, you agree to our{' '}
                                <button type="button" onClick={() => setLegalModal('terms')} className="text-gray-500 hover:underline underline-offset-2 bg-transparent border-0 p-0 cursor-pointer">Terms of Service</button>
                                {' '}and{' '}
                                <button type="button" onClick={() => setLegalModal('privacy')} className="text-gray-500 hover:underline underline-offset-2 bg-transparent border-0 p-0 cursor-pointer">Privacy Policy</button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />
        </>
    );
};

export default Login;
